/**
 * A lista de conversa grudada no pé (`lib/rolagem/grudadoNoPe`): quem está no fim continua no fim quando o conteúdo
 * cresce, quem sobe para ler não é puxado de volta. A lista entrega, por `ref`, o elemento que rola e o do conteúdo;
 * o hook liga os dois e devolve `irAoPe`, que leva ao fim e gruda (abrir a conversa, mensagem nova).
 *
 * `suspenso`: outro dono rola a lista (o salto até uma mensagem); enquanto vale, ela não leva ao pé, e ao voltar a
 * `false` o estado sai da geometria.
 *
 * Os `ref` não avisam quando mudam: a cada commit o hook confere se o rolável ou o conteúdo são outros (a lista que só
 * aparece depois do "Carregando…", o `ScrollArea` que remonta) e religa; a conferência é só uma comparação de
 * identidade. Liga em `useLayoutEffect`, antes de pintar e antes dos `useEffect` da lista (o `irAoPe` da abertura).
 *
 * O que a lista precisa oferecer (quem for ligá-lo no atendimento e no portal, PR 3):
 * - `rolavelRef`: o elemento que ROLA (o do `scrollTop`/`scrollHeight` que definem o pé): o `div` com
 *   `overflow-y-auto`, ou o viewport do Radix (`[data-radix-scroll-area-viewport]`, achado pelo atributo; ele nasce com
 *   `overflow-y: hidden` e não dá para procurá-lo pelo estilo). Pode ser `null` enquanto a lista não existe.
 * - `conteudoRef`: UM elemento dentro do rolável que embrulha TUDO o que rola (o tamanho dele é o que cresce). Se as
 *   mensagens são filhas diretas do rolável (o `SessionChat`, o portal), embrulhe-as num `div` (como o fio). No
 *   `ScrollArea` do Radix, o filho do viewport. Não reaproveite um elemento que outro observador usa para se achar (a
 *   `MessageList` observa o invólucro do Radix porque o salto se acha pelo `div.relative.py-4`).
 * - O estado "está no pé" passa a ser do hook: a lista não guarda o seu (`wasNearBottomRef`); para levar ao fim na
 *   abertura ou na mensagem nova, chama `irAoPe`. A soma da página anterior ao `scrollTop` ("Carregar mais") pode
 *   continuar com a lista: escrita de fora não confunde o hook. O `useEntradaSemPulo` do cartão, dentro dela, cede
 *   sozinho (acha a lista pelo registro).
 *
 * 🔴 O feed NÃO é coberto: ele rola a JANELA, e não há elemento rolável a entregar (o `scroll` da janela chega em
 * `window`, não no `document.scrollingElement`, e o topo dela na tela é sempre 0). Lá os cartões seguem nas regras do
 * `useEntradaSemPulo` contra a janela (regra 1 no fim da página, regra 2 só sem âncora do navegador, com o resto da
 * margem por cartão; regra 3). O PR 3 tem de decidir: se o feed fica assim (ele não é lista de "grudar no fim"); se a
 * regra 1 faz sentido no fim da página com a rolagem infinita (manter a pessoa no fim compete com o carregar mais
 * posts); e, se quiser a leitura exata no Safari também ali, ensinar a janela a este hook (ouvinte em `window`, topo 0,
 * observador no contêiner do feed).
 */
import { useCallback, useLayoutEffect, useRef, type RefObject } from "react";

import { grudarNoPe, type ControleDoPe } from "@/lib/rolagem/grudadoNoPe";

interface OpcoesDoGrudado {
  /** O salto (ou outro dono) está rolando a lista. */
  suspenso?: boolean;
}

export function useGrudadoNoPe(
  rolavelRef: RefObject<HTMLElement | null>,
  conteudoRef: RefObject<HTMLElement | null>,
  { suspenso = false }: OpcoesDoGrudado = {}
): { irAoPe: (comportamento?: ScrollBehavior) => void } {
  const controle = useRef<ControleDoPe | null>(null);
  const conteudoLigado = useRef<HTMLElement | null>(null);

  // Sem lista de dependências, de propósito: os `ref` mudam sem render próprio.
  useLayoutEffect(() => {
    const rolavel = rolavelRef.current;
    const conteudo = conteudoRef.current;
    const atual = controle.current;
    if ((atual ? atual.rolavel : null) !== rolavel || conteudoLigado.current !== conteudo) {
      atual?.desligar();
      controle.current = rolavel ? grudarNoPe(rolavel, conteudo) : null;
      conteudoLigado.current = conteudo;
    }
    controle.current?.suspender(suspenso);
  });

  // Desmontar (e o desfaz-refaz do StrictMode) solta tudo; o efeito acima religa na volta.
  useLayoutEffect(
    () => () => {
      controle.current?.desligar();
      controle.current = null;
      conteudoLigado.current = null;
    },
    []
  );

  const irAoPe = useCallback((comportamento?: ScrollBehavior) => {
    controle.current?.irAoPe(comportamento);
  }, []);
  return { irAoPe };
}
